import { ChangeDetectorRef, Component, OnInit, inject } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
  AbstractControl,
  ValidationErrors,
  ValidatorFn,
} from "@angular/forms";
import { AppButtonComponent } from "../../../../Common/Components/Button/ButtonComponent";
import { RichTextEditorComponent } from "../../../../Common/Components/RichTextEditor/RichTextEditorComponent";
import { NgGlyph } from "@ng-icons/core";
import { MaterialListItem } from "../../Dto/MaterialModel";
import { MaterialEffects } from "../../State/MaterialEffects";
import { MaterialState } from "../../State/MaterialState";
import { FileApi } from "../../../File/Api/FileApi";
import { ToastEffects } from "../../../../Common/Components/Toast/State/ToastEffects";
import { ConfirmModalEffects } from "../../../../Common/Components/ConfirmModal/State/ConfirmModalEffects";
import { getBackendErrorMessage } from "../../../../Common/Utility/UtilityError";

@Component({
  selector: "app-material-edit",
  standalone: true,
  imports: [ReactiveFormsModule, AppButtonComponent, RichTextEditorComponent, NgGlyph],
  templateUrl: "./MaterialEditPage.html",
  styleUrl: "./MaterialEditPage.scss",
})
export class MaterialEditPage implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly materialEffects = inject(MaterialEffects);
  private readonly materialState = inject(MaterialState);
  private readonly fileApi = inject(FileApi);
  private readonly toastEffects = inject(ToastEffects);
  private readonly confirmModalEffects = inject(ConfirmModalEffects);
  private readonly fb = inject(FormBuilder);
  private readonly cdr = inject(ChangeDetectorRef);

  readonly materialId = this.route.snapshot.paramMap.get("id") ?? undefined;

  materialForm: FormGroup;

  material: MaterialListItem | null = null;
  isSaving = false;

  get isSavingEntity(): boolean {
    return this.isSaving;
  }

  get canSaveEntity(): boolean {
    return !this.isSaving && !!this.materialId && this.materialForm.valid;
  }

  get avatarName(): string {
    return this.materialForm.get("name")?.value || "New material";
  }

  get avatarSku(): string {
    return this.materialForm.get("sku")?.value || "No SKU yet";
  }

  get avatarImageUrl(): string | null {
    const value = this.materialForm.get("imageUrl")?.value;
    if (value) {
      return value;
    }

    return null;
  }

  priceValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!value) {
        return { required: true };
      }
      const numValue = parseFloat(value);
      if (Number.isNaN(numValue) || numValue < 0) {
        return { min: true };
      }
      return null;
    };
  }

  optionalPositiveIntegerValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (value === null || value === undefined || value === "") {
        return null;
      }
      const parsed = Number(value);
      if (Number.isInteger(parsed) && parsed > 0) {
        return null;
      }

      return { min: true };
    };
  }

  constructor() {
    this.materialForm = this.fb.group({
      name: ["", [Validators.required, Validators.minLength(2)]],
      sku: ["", [Validators.required, Validators.minLength(2)]],
      price: ["", [Validators.required, this.priceValidator()]],
      dcmSeriesId: ["", [this.optionalPositiveIntegerValidator()]],
      imageUrl: [""],
      description: [""],
    });
  }

  async ngOnInit(): Promise<void> {
    if (!this.materialId) {
      await this.router.navigate(["/materials"]);
      return;
    }

    await this.fetchMaterial();
  }

  private async fetchMaterial(): Promise<void> {
    if (!this.materialId) {
      this.toastEffects.show("Material ID is required.", "error");
      await this.router.navigate(["/materials"]);
      return;
    }

    const material = this.materialState.materialById()(this.materialId);
    if (material) {
      this.material = material;
      this.patchFormFromMaterial(material);
      this.cdr.detectChanges();
      return;
    }

    await this.materialEffects.searchMaterials();
    const refreshedMaterial = this.materialState.materialById()(this.materialId);
    if (!refreshedMaterial) {
      this.toastEffects.show("Material not found.", "error");
      await this.router.navigate(["/materials"]);
      return;
    }

    setTimeout(() => {
      this.material = refreshedMaterial;
      this.patchFormFromMaterial(refreshedMaterial);
      this.cdr.detectChanges();
    }, 0);
  }

  async saveMaterial(): Promise<void> {
    if (this.materialForm.invalid) {
      setTimeout(() => {
        this.materialForm.markAllAsTouched();
      });
      this.toastEffects.show("Please fill in all required fields.", "error");
      return;
    }

    if (!this.materialId) {
      this.toastEffects.show("Missing material identifier.", "error");
      return;
    }

    this.isSaving = true;
    this.cdr.detectChanges();
    const formData = this.materialForm.value;

    const updateData: Partial<MaterialListItem> = {
      name: formData.name,
      sku: formData.sku,
      price: parseFloat(formData.price),
      dcmSeriesId: this.parseOptionalInteger(formData.dcmSeriesId),
    };

    let imageUrl = "";
    if (typeof formData.imageUrl === "string") {
      imageUrl = formData.imageUrl.trim();
    }
    if (imageUrl) {
      updateData.imageUrl = imageUrl;
    }

    let description: string | undefined;
    if (typeof formData.description === "string") {
      description = formData.description.trim();
    }
    if (description !== undefined) {
      updateData.description = description || null;
    }

    try {
      const updatedMaterial = await this.materialEffects.updateMaterial(this.materialId, updateData);

      setTimeout(() => {
        this.material = updatedMaterial;
        this.patchFormFromMaterial(updatedMaterial);
        this.toastEffects.show("Material updated", "success");
        this.cdr.detectChanges();
      }, 0);
    } catch (exception: unknown) {
      this.toastEffects.show(getBackendErrorMessage(exception, "Unable to save material."), "error");
    } finally {
      setTimeout(() => {
        this.isSaving = false;
        this.cdr.detectChanges();
      }, 0);
    }
  }

  async onAvatarChange(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      return;
    }

    if (!this.materialId) {
      this.toastEffects.show("Missing material identifier.", "error");
      return;
    }

    const file = input.files[0];

    try {
      const uploadedFile = await this.fileApi.uploadFile(file, { folder: "materials" });
      const imageUrl = uploadedFile.publicUrl;
      const updatedMaterial = await this.materialEffects.updateMaterial(this.materialId, { imageUrl });

      setTimeout(() => {
        this.material = updatedMaterial;
        this.patchFormFromMaterial(updatedMaterial);
        this.cdr.detectChanges();
      }, 0);
    } catch (exception: unknown) {
      this.toastEffects.show(getBackendErrorMessage(exception, "Failed to update image"), "error");
    } finally {
      input.value = "";
    }
  }

  async removeAvatar(): Promise<void> {
    if (!this.materialId || !this.material?.imageUrl) {
      return;
    }
    const material = this.material;

    this.confirmModalEffects.open({
      title: "Remove material image?",
      message: `This will permanently remove the image from ${material.name}.`,
      confirmText: "Remove image",
      cancelText: "Cancel",
      variant: "danger",
      closeOnOverlay: true,
      onConfirm: async () => {
        if (!this.materialId) {
          this.toastEffects.show("Missing material identifier.", "error");
          return;
        }

        try {
          await this.materialEffects.deleteMaterialImage(this.materialId);
          const updatedMaterial: MaterialListItem = { ...material, imageUrl: null };

          setTimeout(() => {
            this.material = updatedMaterial;
            this.patchFormFromMaterial(updatedMaterial);
            this.toastEffects.show("Image removed", "success");
            this.cdr.detectChanges();
          }, 0);
        } catch (exception: unknown) {
          this.toastEffects.show(getBackendErrorMessage(exception, "Failed to remove image"), "error");
        }
      },
    });
  }

  goBack(): void {
    this.router.navigate(["/materials"]).catch(() => undefined);
  }

  private patchFormFromMaterial(material: MaterialListItem): void {
    if (this.materialForm.dirty) {
      return;
    }

    this.materialForm.patchValue(
      {
        name: material.name,
        sku: material.sku,
        price: material.price.toString(),
        dcmSeriesId: material.dcmSeriesId?.toString() ?? "",
        imageUrl: material.imageUrl ?? "",
        description: material.description ?? "",
      },
      { emitEvent: false },
    );
  }

  private parseOptionalInteger(value: unknown): number | null {
    if (value === null || value === undefined || value === "") {
      return null;
    }
    const parsed = Number(value);
    if (Number.isInteger(parsed) && parsed > 0) {
      return parsed;
    }

    return null;
  }
}
